import { useEffect } from "react";

// Singleton registry of mounted video slot elements
const registeredSlots = new Set();
let activeSlotElement = null;
let scrollListenerAttached = false;
let rafId = null;
let intersectionObserver = null;
let isHostRendered = false;

/**
 * Claims rendering of the persistent player host.
 * Always returns true on SSR. On client, returns true only once per story session.
 */
export function claimHostRender() {
  if (typeof window === "undefined") {
    return true;
  }
  if (!isHostRendered) {
    isHostRendered = true;
    return true;
  }
  return false;
}

/**
 * Resets the host render claim flag when transitioning between stories/pages.
 */
export function resetHostRender() {
  isHostRendered = false;
}

/**
 * Resolves the root story container for a given video slot.
 */
export function getStoryRoot(targetSlot) {
  if (!targetSlot || typeof targetSlot.closest !== "function") return null;
  return targetSlot.closest(
    '[data-infinite-scroll]:not([data-infinite-scroll="load-more"]), .story-row-container, [data-story-slug]'
  );
}

/**
 * Updates the active story root z-index by toggling .has-active-story-video.
 * Only the root of the story holding the active video gets .has-active-story-video (z-index: 2),
 * ensuring it paints above sibling stories (z-index: 0) while staying below sticky header (z-index: 100).
 */
export function updateStoryRootZIndex(targetSlot) {
  if (typeof document === "undefined") return;

  const activeRoot = targetSlot ? getStoryRoot(targetSlot) : null;

  const currentActiveRoots = document.querySelectorAll(".has-active-story-video");
  currentActiveRoots.forEach((el) => {
    if (el !== activeRoot) {
      el.classList.remove("has-active-story-video");
    }
  });

  if (activeRoot && !activeRoot.classList.contains("has-active-story-video")) {
    activeRoot.classList.add("has-active-story-video");
  }
}

/**
 * Returns an array of registered slot elements in document order.
 */
function getOrderedSlots() {
  const elements = Array.from(registeredSlots).filter((el) => el && el.isConnected);
  return elements.sort((a, b) => {
    const position = a.compareDocumentPosition(b);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
}

/**
 * Resets the floating styling applied to the SendToNews / Voltax player.
 */
export function resetVoltaxFloatingState(playerHost) {
  if (!playerHost || typeof window === "undefined") return;

  // 1. Remove inline fixed position and floating classes added by Voltax / STN
  try {
    const floatingNodes = playerHost.querySelectorAll(
      '[style*="position: fixed"], [style*="position:fixed"], .s2n-float, [data-type="float"], [class*="s2nPlayer"], [class*="s2nContainer"]'
    );
    floatingNodes.forEach((node) => {
      if (node.style && (node.style.position === "fixed" || node.style.position === "fixed !important")) {
        node.style.position = "";
      }
      if (node.classList) {
        node.classList.remove("s2n-float");
        node.classList.remove("s2nContainerAnchor");
      }
      if (typeof node.removeAttribute === "function" && node.getAttribute("data-type") === "float") {
        node.removeAttribute("data-type");
      }
    });
  } catch (err) {
    // Ignore DOM styling errors
  }

  // 2. Notify Voltax player API if available on window
  try {
    const voltaxPlayer = window?.voltax?.players?.[0];
    if (voltaxPlayer && typeof voltaxPlayer.setFloatingEnabled === "function") {
      voltaxPlayer.setFloatingEnabled(false);
      setTimeout(() => {
        try {
          voltaxPlayer.setFloatingEnabled(true);
        } catch (e) {
          // Ignore API error
        }
      }, 50);
    }
  } catch (e) {
    // Ignore API error
  }

  // 3. Dispatch a scroll event to trigger Voltax's internal scroll recalculation
  try {
    window.dispatchEvent(new Event("scroll"));
  } catch (e) {
    // Ignore in non-browser env
  }
}

/**
 * Transfers the single persistent player host into the target slot container.
 */
export function transferPlayerHostToSlot(targetSlot) {
  if (!targetSlot || typeof document === "undefined") return;

  const playerHost = document.getElementById("stn-story-player-host");
  if (!playerHost) return;

  if (playerHost.parentElement === targetSlot) {
    updateStoryRootZIndex(targetSlot);
    return;
  }

  targetSlot.appendChild(playerHost);
  activeSlotElement = targetSlot;

  resetVoltaxFloatingState(playerHost);
  updateStoryRootZIndex(targetSlot);
}

/**
 * Evaluates which story slot should currently host the video player.
 * Only transfers when an eligible slot enters the viewport.
 * When no slot is in view, the floating player stays anchored to activeSlotElement.
 */
export function evaluateActiveSlot() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const playerHost = document.getElementById("stn-story-player-host");
  if (!playerHost) return;

  const slots = getOrderedSlots();
  if (slots.length === 0) return;

  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const midPoint = windowHeight / 2;

  // Check if any slot is currently inside the viewport
  let bestSlot = null;
  let bestDistance = Infinity;

  for (const slot of slots) {
    const rect = slot.getBoundingClientRect();
    if (rect.top < windowHeight && rect.bottom > 0) {
      const distance = Math.abs(rect.top + rect.height / 2 - midPoint);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestSlot = slot;
      }
    }
  }

  if (bestSlot) {
    transferPlayerHostToSlot(bestSlot);
    return;
  }

  // If no slot is in the viewport, the video is floating.
  // Ensure the active story keeps .has-active-story-video so it paints above sibling stories.
  if (activeSlotElement || playerHost.parentElement) {
    updateStoryRootZIndex(activeSlotElement || playerHost.parentElement);
  }
}

function handleThrottledScroll() {
  if (rafId) return;
  rafId = window.requestAnimationFrame(() => {
    rafId = null;
    evaluateActiveSlot();
  });
}

function initIntersectionObserver() {
  if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;
  if (intersectionObserver) return;

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      let shouldEvaluate = false;
      for (const entry of entries) {
        if (entry.isIntersecting) {
          shouldEvaluate = true;
          break;
        }
      }
      if (shouldEvaluate) {
        evaluateActiveSlot();
      }
    },
    { threshold: [0, 0.25, 0.5, 0.75, 1] }
  );
}

function startGlobalListeners() {
  if (typeof window === "undefined") return;
  if (!scrollListenerAttached) {
    window.addEventListener("scroll", handleThrottledScroll, { passive: true });
    window.addEventListener("resize", handleThrottledScroll, { passive: true });
    scrollListenerAttached = true;
  }
  initIntersectionObserver();
}

/**
 * Registers a story video slot element.
 */
export function registerVideoSlot(slotElement) {
  if (!slotElement) return;

  registeredSlots.add(slotElement);
  startGlobalListeners();

  if (intersectionObserver) {
    intersectionObserver.observe(slotElement);
  }

  // Ensure activeSlotElement is tracked
  const playerHost = typeof document !== "undefined" ? document.getElementById("stn-story-player-host") : null;
  if (!activeSlotElement) {
    if (playerHost && playerHost.parentElement) {
      activeSlotElement = playerHost.parentElement;
    } else {
      activeSlotElement = slotElement;
    }
    updateStoryRootZIndex(activeSlotElement);
  } else if (playerHost && playerHost.parentElement === slotElement) {
    updateStoryRootZIndex(slotElement);
  }
}

/**
 * Unregisters a story video slot element.
 */
export function unregisterVideoSlot(slotElement) {
  if (!slotElement) return;

  registeredSlots.delete(slotElement);

  if (intersectionObserver) {
    try {
      intersectionObserver.unobserve(slotElement);
    } catch (e) {
      // Ignore unobserve errors
    }
  }

  if (activeSlotElement === slotElement) {
    activeSlotElement = null;
    const remaining = getOrderedSlots();
    if (remaining.length > 0) {
      transferPlayerHostToSlot(remaining[0]);
    } else {
      updateStoryRootZIndex(null);
    }
  }
}

/**
 * Explicit trigger when Quintype's onItemFocus notifies that a story came into view.
 */
export function notifyStoryInView(storySlugOrId) {
  if (!storySlugOrId || typeof document === "undefined") return;

  const slots = getOrderedSlots();
  for (const slot of slots) {
    const storyRoot = getStoryRoot(slot);
    if (storyRoot) {
      const slug = storyRoot.getAttribute("data-story-slug");
      const id = storyRoot.getAttribute("data-story-id");
      if (slug === storySlugOrId || id === String(storySlugOrId)) {
        transferPlayerHostToSlot(slot);
        return;
      }
    }
  }

  // Fallback to evaluating by viewport coordinates
  evaluateActiveSlot();
}

/**
 * Custom React hook for registering and managing a story video slot.
 */
export function useStoryVideoSlot(slotRef) {
  useEffect(() => {
    const element = slotRef.current;
    if (!element) return;

    registerVideoSlot(element);

    return () => {
      unregisterVideoSlot(element);
    };
  }, [slotRef]);
}

export const __TEST_ONLY__ = {
  registeredSlots,
  getOrderedSlots,
  resetVoltaxFloatingState,
  evaluateActiveSlot,
  resetHostRender,
};
